import React, { useState } from 'react';
import { X, Send, AlertCircle, Edit3, CheckCircle } from 'lucide-react';

interface ResubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (feedback: string) => void;
  publicationTitle: string;
  isLoading?: boolean;
}

const ResubmitModal: React.FC<ResubmitModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  publicationTitle,
  isLoading = false
}) => {
  const [feedback, setFeedback] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const feedbackTemplates = [
    {
      id: 'structure',
      title: 'Structure & Organization',
      template: `The manuscript structure needs improvement. Please consider:

• Reorganizing the introduction to better establish the research gap
• Strengthening the methodology section with more detailed procedures
• Improving the flow between sections
• Adding clearer transitions between paragraphs

Please revise and resubmit.`
    },
    {
      id: 'methodology',
      title: 'Methodology Issues',
      template: `The methodology section requires clarification:

• Provide more detailed experimental procedures
• Include sample size calculations and power analysis
• Clarify data collection methods
• Add information about statistical analysis approach

Please address these methodological concerns and resubmit.`
    },
    {
      id: 'analysis',
      title: 'Data Analysis',
      template: `The data analysis needs improvement:

• Include more comprehensive statistical tests
• Provide effect sizes and confidence intervals
• Add appropriate visualizations for the data
• Discuss limitations of the analysis

Please enhance the analysis section and resubmit.`
    },
    {
      id: 'writing',
      title: 'Writing & Clarity',
      template: `The writing needs improvement for clarity:

• Simplify complex sentences
• Define technical terms on first use
• Improve paragraph structure
• Check for grammatical errors and typos

Please revise for clarity and resubmit.`
    },
    {
      id: 'references',
      title: 'References & Citations',
      template: `The references section needs attention:

• Update outdated references with more recent studies
• Ensure all in-text citations are properly formatted
• Include key papers that may have been missed
• Verify DOI links and publication details

Please update references and resubmit.`
    }
  ];

  const handleTemplateSelect = (template: string) => {
    setFeedback(template);
    setSelectedTemplate(template);
  };

  const handleSubmit = () => {
    if (feedback.trim()) {
      onConfirm(feedback.trim());
    }
  };

  const handleClose = () => {
    setFeedback('');
    setSelectedTemplate('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Edit3 className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Resubmit Publication</h2>
              <p className="text-sm text-gray-600">Provide feedback for revision</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Publication Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Publication to Resubmit:</h3>
            <p className="text-gray-700">{publicationTitle}</p>
          </div>

          {/* Feedback Templates */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Quick Templates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {feedbackTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.template)}
                  className={`p-3 text-left border rounded-lg transition-colors ${
                    selectedTemplate === template.template
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">{template.title}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {template.template.substring(0, 100)}...
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Feedback */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feedback for Author
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              placeholder="Provide detailed feedback for the author. You can use the templates above as a starting point or write your own feedback..."
            />
            <div className="mt-2 text-sm text-gray-500">
              {feedback.length} characters
            </div>
          </div>

          {/* User Guidance */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• The author will receive an email with your feedback</li>
                  <li>• The publication status will change to "Needs Resubmission"</li>
                  <li>• The author can edit their publication and resubmit it</li>
                  <li>• The publication will return to "Pending Review" status</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!feedback.trim() || isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Send for Resubmission</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResubmitModal;
